import { Component, createEffect, createSignal, For } from "solid-js";
import { CivFlag } from "../../explorer/src/components/CivFlag";
import { Icon } from "../../explorer/src/components/Icon";
import { DataSeries, TimelineChart } from "./Charts";
import { populationValuesPerInterval } from "./parsing/parseUtils";
import { GameSummary, ResourceType, resourceTypeLiterals } from "./parsing/types";

function getDataseriesForResource(resource: ResourceType, players: GameSummary[]): DataSeries[] {
  return players.map((player) => {
    const data = player.resources[resource] || [];
    return {
      label: player.name,
      data,
      color: player.color,
    };
  });
}

function getDataseriesForTotalResources(players: GameSummary[]): DataSeries[] {
  return players.map((player) => {
    const data = player.resources.timestamps.map((timestamp, index) =>
      resourceTypeLiterals.reduce((acc, resource) => acc + (player.resources[resource]?.[index] || 0), 0)
    );
    return {
      label: player.name,
      data,
      color: player.color,
    };
  });
}

function getDataseriesForTotalResourcesPerMin(players: GameSummary[]): DataSeries[] {
  return players.map((player) => {
    const data = player.resources.timestamps.map((timestamp, index) =>
      resourceTypeLiterals.reduce((acc, resource) => acc + (player.resources[resource+'PerMin']?.[index] || 0), 0)
    );
    return {
      label: player.name,
      data,
      color: player.color,
    };
  });
}

function getDataseriesForTotalResourcesGathered(players: GameSummary[]): DataSeries[] {
  return players.map((player) => {
    const data = player.resources.timestamps.map((timestamp, index) =>
      resourceTypeLiterals.reduce((acc, resource) => acc + (player.resources[resource+'Gathered']?.[index] || 0), 0)
    );
    return {
      label: player.name,
      data,
      color: player.color,
    };
  });
}

function getDataseriesForPopulation(players: GameSummary[]): Record<"workers" | "army" | "popcap", DataSeries[]> {
  const series = {
    workers: [],
    army: [],
    popcap: [],
  };
  for (const player of players) {
    Object.entries(populationValuesPerInterval(player.items, player.resources.timestamps)).forEach(([key, data]) => {
      series[key].push({
        label: player.name,
        data,
        color: player.color,
      });
    });
  }

  return series;
}

const views: Record<string, { label: string }> = {
  army: {
    label: "Army Value",
  },
  workers: {
    label: "Worker Count",
  },
  popcap: {
    label: "Population Cap",
  },
  total: { label: "Total Score" },
  military: { label: "Military Score" },
  economy: { label: "Economy Score" },
  technology: { label: "Technology Score" },
  society: { label: "Society Score" },
  resources: { label: "Total Resource Balance" },
  food: { label: "Food Balance" },
  wood: { label: "Wood Balance" },
  gold: { label: "Gold Balance" },
  stone: { label: "Stone Balance" },
  oliveoil: { label: "Olive Oil Balance" },
  resourcesPerMin: { label: "Total Resources/min" },
  foodPerMin: { label: "Food/min" },
  woodPerMin: { label: "Wood/min" },
  goldPerMin: { label: "Gold/min" },
  stonePerMin: { label: "Stone/min" },
  oliveoilPerMin: { label: "Olive Oil/min" },
  // These sometimes go down, gotta be a bug on the game side, let's hide em for now to avoid confusion
  //resourcesGathered: { label: "Total Resources Gathered" },
  //foodGathered: { label: "Food Gathered" },
  //woodGathered: { label: "Wood Gathered" },
  //goldGathered: { label: "Gold Gathered" },
  //stoneGathered: { label: "Stone Gathered" },
  //oliveoilGathered: { label: "Olive Oil Gathered" },
};

export const TimelineSkeleton = () => {
  return (
    <div class="flex mx-4">
      <div class="basis-1/4 mr-4" />
      <div class="basis-3/4">
        <div class="bg-black/20 rounded p-2 my-4 grid place-items-center text-gray-200 " style={{ height: "490px" }}>
          Loading...
        </div>
      </div>
    </div>
  );
};

export const Timeline: Component<{ players: GameSummary[] }> = (props) => {
  const [view, setView] = createSignal("army");
  const [graph, setGraph] = createSignal<DataSeries[]>([]);
  const [series, setSeries] = createSignal<{ highlight: boolean; hidden: boolean }[]>(props.players.map(() => ({ highlight: false, hidden: false })));

  createEffect(() => {
    if (["food", "wood", "gold", "stone", "oliveoil", "foodPerMin", "woodPerMin", "goldPerMin", "stonePerMin", "oliveoilPerMin", "foodGathered", "woodGathered", "goldGathered", "stoneGathered", "oliveoilGathered", "total", "military", "economy", "society", "technology"].includes(view()))
      setGraph(getDataseriesForResource(view() as ResourceType, props.players));
    if (view() == "resources") setGraph(getDataseriesForTotalResources(props.players));
    if (view() == "workers") setGraph(getDataseriesForPopulation(props.players).workers);
    if (view() == "army") setGraph(getDataseriesForPopulation(props.players).army);
    if (view() == "popcap") setGraph(getDataseriesForPopulation(props.players).popcap);
    if (view() == "resourcesPerMin") setGraph(getDataseriesForTotalResourcesPerMin(props.players));
    if (view() == "resourcesGathered") setGraph(getDataseriesForTotalResourcesGathered(props.players));
  });

  const highlightSeries = (index: number) => setSeries(series()?.map((s, i) => ({ ...s, highlight: i == index && !s.hidden })));

  const toggleSeries = (index: number) => setSeries(series()?.map((s, i) => ({ ...s, highlight: false, hidden: i == index ? !s.hidden : s.hidden })));

  return (
    <div class="flex mx-4">
      <div class="basis-1/4 mr-4">
        <div class="relative mt-4">
          <select
            class="w-full text-gray-100 bg-gray-600 border-gray-400 appearance-none block pl-6 pr-10 py-2 font-bold placeholder-gray-900 border rounded-full focus:outline-none"
            onChange={(e) => setView(e.currentTarget.value)}
          >
            <For each={Object.entries(views)}>{([key, view]) => <option value={key}>{view.label}</option>}</For>
          </select>
          <Icon icon="chevron-down" class="pointer-events-none absolute inset-y-0 right-3 flex items-center px-2 text-gray-100" />
        </div>

        <div class="mt-4">
          <For each={props.players}>
            {(player, i) => {
              const hidden = () => series()[i()].hidden;

              return (
                <div class="flex items-center" style={{ opacity: hidden() ? 0.5 : 1 }}>
                  <button style={{ color: player.color }} class="mr-2 flex-none" onClick={() => toggleSeries(i())}>
                    {series()[i()].hidden ? <Icon icon="circle" /> : <Icon icon="circle-check" />}
                  </button>
                  <div class="text-gray-100 font-bold flex-auto py-1" onMouseEnter={() => highlightSeries(i())} onMouseLeave={() => highlightSeries(-1)}>
                    {player.name}
                  </div>
                  <CivFlag abbr={player.civ.abbr} class="w-8 rounded ml-auto" />
                </div>
              );
            }}
          </For>
        </div>
      </div>
      <div class="basis-3/4">
        <TimelineChart
          data={graph()}
          timestamps={props.players[0].resources.timestamps}
          title={views[view()].label}
          series={series()}
          options={{ xGridLines: true, yGridLines: true }}
        />
      </div>
    </div>
  );
};
